import * as Sentry from '@sentry/react';

/**
 * Shared plumbing for getting UI failures into Sentry.
 *
 * Sentry's global handlers only see what actually escapes to the browser --
 * uncaught render errors and unhandled rejections. Anything this app catches
 * itself (every RTK Query failure, every `try/catch` around a fetch) is
 * invisible unless it is reported explicitly, which is what this module is for.
 */

/** Only the id -- names and emails stay out of Sentry. */
export const currentUserId = (): string | undefined => {
  try {
    const stored = localStorage.getItem('user');
    const id = stored ? JSON.parse(stored)?.id : undefined;
    return id === undefined || id === null ? undefined : String(id);
  } catch {
    return undefined;
  }
};

export const currentApp = (): 'parent' | 'staff' =>
  location.pathname.startsWith('/parent') ? 'parent' : 'staff';

/**
 * Turns whatever was thrown into a real `Error`.
 *
 * `authenticatedFetch` throws the raw response body and RTK Query error objects
 * get re-thrown by `.unwrap()`, so a lot of what reaches Sentry is a plain
 * object. Sentry titles those `<unknown>` or "Object captured as exception with
 * keys: ..." and attaches no stack, which makes them untriageable -- give them
 * a real message first.
 */
export const toError = (thrown: unknown): Error => {
  if (thrown instanceof Error) return thrown;

  if (typeof thrown === 'string') return new Error(thrown);

  if (thrown && typeof thrown === 'object') {
    // Backend text can name a student or an amount and Sentry is a third
    // party, so the message says only which key carried the reason.
    const candidate = thrown as Record<string, any>;
    const carrier =
      candidate.errors?.[0] != null
        ? 'errors'
        : candidate.detail != null
          ? 'detail'
          : candidate.message != null
            ? 'message'
            : typeof candidate.error === 'string'
              ? 'error'
              : 'none';

    const error = new Error(
      `Non-Error thrown (${carrier}) with keys: ${Object.keys(candidate).join(', ')}`
    );
    error.name = 'NonErrorThrown';
    return error;
  }

  return new Error(`Non-Error thrown: ${String(thrown)}`);
};

const REPORTED = '__sentryReported';

/**
 * Marks a thrown value as already reported.
 *
 * `.unwrap()` re-throws the very object `baseQuery` already reported, so a
 * `catch` that also calls `reportError` would file the same failure twice.
 * Marking is best-effort -- a frozen or non-object value just stays unmarked
 * and gets reported again, which is the safe direction to fail in.
 */
export const markReported = (thrown: unknown): void => {
  if (!thrown || typeof thrown !== 'object') return;
  try {
    Object.defineProperty(thrown, REPORTED, {
      value: true,
      enumerable: false,
      configurable: true,
    });
  } catch {
    // Frozen or sealed -- nothing to do.
  }
};

export const wasReported = (thrown: unknown): boolean =>
  !!thrown &&
  typeof thrown === 'object' &&
  (thrown as Record<string, unknown>)[REPORTED] === true;

interface ReportContext {
  /** Where this was caught, e.g. `useDownloadPreview.downloadAndPreviewPdf`. */
  where: string;
  extra?: Record<string, unknown>;
  tags?: Record<string, string>;
  level?: 'error' | 'warning';
}

/**
 * Reports a caught error to Sentry. Safe to call from any `catch` block --
 * it never throws and is a no-op outside production.
 */
export const reportError = (thrown: unknown, context: ReportContext) => {
  if (process.env.NODE_ENV !== 'production') return;
  if (wasReported(thrown)) return;

  try {
    markReported(thrown);
    Sentry.captureException(toError(thrown), {
      level: context.level ?? 'error',
      user: { id: currentUserId() },
      tags: { app: currentApp(), where: context.where, ...context.tags },
      extra: context.extra,
    });
  } catch {
    // Reporting must never break the flow that was already failing.
  }
};
